/**
 * 쿠키 관련 유틸리티 함수들
 */

/**
 * 쿠키 값을 가져옵니다
 * @param {string} name - 쿠키 이름
 * @returns {string|null} 쿠키 값 또는 null
 */
export function getCookie(name) {
  if (typeof document === 'undefined') {
    return null
  }

  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  
  return null
}

/**
 * 쿠키를 설정합니다
 * @param {string} name - 쿠키 이름
 * @param {string} value - 쿠키 값
 * @param {Object} [options] - 쿠키 옵션
 * @param {number} [options.days] - 만료 일수
 * @param {string} [options.path] - 경로 (기본값: '/')
 * @param {string} [options.domain] - 도메인
 * @param {boolean} [options.secure] - HTTPS에서만 전송
 * @param {string} [options.sameSite] - SameSite 속성 ('Strict', 'Lax', 'None')
 */
export function setCookie(name, value, options = {}) {
  if (typeof document === 'undefined') {
    return
  }

  const {
    days,
    path = '/',
    domain,
    secure,
    sameSite = 'Lax'
  } = options

  let cookieString = `${name}=${encodeURIComponent(value)}`

  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    cookieString += `; expires=${date.toUTCString()}`
  }

  if (path) {
    cookieString += `; path=${path}`
  }

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  if (secure) {
    cookieString += `; secure`
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`
  }

  document.cookie = cookieString
}

/**
 * 쿠키를 삭제합니다
 * @param {string} name - 쿠키 이름
 * @param {Object} options - 쿠키 옵션 (path, domain)
 */
export function deleteCookie(name, options = {}) {
  setCookie(name, '', { ...options, days: -1 })
}

/**
 * 모든 쿠키를 가져옵니다
 * @returns {Object} 쿠키 객체
 */
export function getAllCookies() {
  if (typeof document === 'undefined') {
    return {}
  }

  const cookies = {}
  
  document.cookie.split(';').forEach(cookie => {
    const [name, value] = cookie.trim().split('=')
    if (name && value) {
      cookies[name] = decodeURIComponent(value)
    }
  })

  return cookies
}

/**
 * 쿠키 존재 여부를 확인합니다
 * @param {string} name - 쿠키 이름
 * @returns {boolean} 존재 여부
 */
export function hasCookie(name) {
  return getCookie(name) !== null
}

/**
 * 세션 쿠키를 설정합니다 (브라우저 종료 시 삭제)
 * @param {string} name - 쿠키 이름
 * @param {string} value - 쿠키 값
 * @param {Object} options - 쿠키 옵션
 */
export function setSessionCookie(name, value, options = {}) {
  setCookie(name, value, { ...options, days: undefined })
}